import {
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Box,
  FormControl,
  Select,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  DeleteOutlined,
  ForwardToInboxOutlined,
  GroupRemove,
  LinkOutlined,
  MoreVert,
  RateReviewOutlined,
  Check,
  Close,
  Edit,
  PersonAdd,
  PhoneCallback,
  PhoneDisabledOutlined,
} from '@mui/icons-material';
import ConfirmationModal from '@/components/common/confirmation-modal';
import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions.ts';
import { useEventStore } from '@/state/stores/use-event-store';
import { CONTRIBUTOR_PERMISSIONS, EVENT_PERMISSIONS } from '@/enums/permissions.enum';
import { useDeleteEventContributorRoles } from '@/state/mutations/contributors/use-delete-event-contributor-roles.ts';
import { useNotifications } from '@/hooks/use-notifications';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useDeleteSessionContributor } from '@/state/mutations/sessions/use-delete-session-contributor.ts';
import { ContributorRoles } from '@/components/events/detail/tabs/contributors/contributor-roles.tsx';
import { useDeleteContributor } from '@/state/mutations/contributors/use-delete-contributor';
import { usePatchContributorMutation } from '@/state/mutations/contributors/use-patch-contributor';
import { useResendContributorInvitation } from '@/state/mutations/invitations/use-resend-contributor-invitation.ts';
import { useDeleteFlowContributor } from '@/state/mutations/flows/use-delete-flow-contributor';
import { getObjectId } from '@/helpers/object.id.helper';
import { HighlightedText } from '@/components/common/highlighted-text';
import { z } from 'zod';
import { AddCircleOutlined, RemoveCircleOutlined } from '@mui/icons-material';
import { useCreateContributorRoleSlotMutation } from '@/state/mutations/contributors/use-create-contributor-role-slot';
import { useCreateSessionRoleSlotsForAllEventSlotsMutation } from '@/state/mutations/contributors/use-create-session-role-slots-all-event-slots';
import { useDeleteContributorRoleSlotMutation } from '@/state/mutations/contributors/use-delete-contributor-role-slot';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useDeleteContributorRoleSlotsByEventSlotMutation } from '@/state/mutations/contributors/use-delete-contributor-role-slots-by-event-slot';
import { useSearchParams } from 'react-router';
import { EVENT_SLOT_TYPE } from '@/types/dtos/events/event-slot-content.enum';
import { useCreateContributorFlowRoleSlotByEventSlotMutation } from '@/state/mutations/contributors/use-create-contributor-flow-role-slot-by-event-slot';
import { useScreenSize } from '@/hooks/use-screen-size';

import { CREW_ROLES } from '@/types/dtos/contributors/response/contributor.dto';
import { ProfilePhotoAvatar } from '@/components/common/profile-photo-avatar';
import { getContributorInvitationRingColor } from '@/components/events/detail/tabs/contributors/contributor-invitation-icon';
import { contributorHasInviteableStagedSelection } from '@/components/events/detail/tabs/contributors/staging-invite-build.helpers';
import type { InvitePublicationUiContext } from '@/utils/contributor-invite-publication.utils';
import { getContributorProfileReviewIconColor } from '@/components/events/detail/tabs/sessions/session-actors/speaker-profile-review-status';
import { ContributorEventRemoteSpeakerHeaderIcon } from '@/components/events/detail/tabs/contributors/contributor-event-remote-speaker-header-icon';
import { contributorRemoteSpeakerActionLabel } from '@/components/events/detail/tabs/common/contributors/contributor-item-action-labels';
import { RemoteSpeakerConfigureModal } from '@/components/events/detail/tabs/common/contributors/remote-speaker-configure.modal';
import { RemoteSpeakerScopeModal } from '@/components/events/detail/tabs/common/contributors/remote-speaker-scope.modal';
import { useConfigureRemoteSpeakerMutation } from '@/state/mutations/contributors/use-configure-remote-speaker';
import { resolveFlowRemoteSpeakerMenuShowsRemote } from '@/utils/remote-speaker-flow-toggle.utils';
import { resolveContributorEventRemoteSpeakerHeaderIcon } from '@/utils/remote-speaker-status.utils';
import {
  RemoteSpeakerDayEntitlementModal,
  useRemoteSpeakerDayEntitlementModalState,
} from '@/components/events/detail/tabs/common/contributors/remote-speaker-day-entitlement.modal';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { REMOTE_SPEAKERSHIP_FEATURE } from '@/constants/feature-flags';

/** Larger hit area on small screens (WCAG ~44px), compact on `sm+`. */
const contributorRowHeaderIconSx = {
  p: { xs: 1, sm: 0.25 },
  minWidth: { xs: 44, sm: 34 },
  minHeight: { xs: 44, sm: 34 },
} as const;

const MIN_DISPLAY_NAME_LENGTH = 5;
const isValidDisplayName = (displayName: string): boolean => {
  return displayName.trim().length >= MIN_DISPLAY_NAME_LENGTH;
};

const emailSchema = z.string().email();
const phoneSchema = z.string().regex(/^[+]?[0-9\s\-()]{7,}$/);
const normalizeId = (value?: unknown) => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ('id' in value && typeof (value as { id?: string }).id === 'string') {
      return (value as { id: string }).id;
    }
    if ('_id' in value && typeof (value as { _id?: string })._id === 'string') {
      return (value as { _id: string })._id;
    }
    if ('$oid' in value && typeof (value as { $oid?: string }).$oid === 'string') {
      return (value as { $oid: string }).$oid;
    }
  }
  return undefined;
};

export const ContributorRow = ({
  contributor,
  onOpenEventProfileEditor,
  onInviteContributor,
  onCopyInviteLink,
  onContributionSelected,
  isNewContributor = false,
  onSaveNew,
  onCancelNew,
  isInlineEditing = false,
  onStartInlineEdit,
  onStopInlineEdit,
  searchTerm = '',
  flowContext,
  invitePublicationCtx,
}: {
  contributor: ContributorDto;
  onOpenEventProfileEditor?: () => void;
  onInviteContributor: () => void;
  onCopyInviteLink: () => void;
  onContributionSelected: ({
    isSelected,
    contributorId,
    roleId,
    slotId,
  }: {
    isSelected: boolean;
    contributorId: string;
    roleId?: string;
    slotId?: string;
  }) => void;
  isNewContributor?: boolean;
  onSaveNew?: (contributorData: {
    displayName: string;
    organization: string;
    title: string;
    email: string;
    phoneNumber: string;
    crewRole?: string;
  }) => void;
  onCancelNew?: () => void;
  isInlineEditing?: boolean;
  onStartInlineEdit?: () => void;
  onStopInlineEdit?: () => void;
  searchTerm?: string;
  flowContext?: FlowDto;
  /** When set, staged-invite header actions match invite modal (flow slot + ORGANIZE publication filters). */
  invitePublicationCtx?: InvitePublicationUiContext;
}) => {
  const notification = useNotifications();
  const { t } = useTranslation();
  const { data: permissions } = usePermissionsQuery();
  const event = useEventStore((s) => s.event);
  const eventPlanSlots = useEventStore((s) => s.eventPlan?.slots);
  const hasPlanSlotData = !!eventPlanSlots;
  const eventId = useEventStore((s) => s.event?.id);
  const eventIdForPermissions = event?.origin ? getObjectId(event.origin) : event?.id;

  // Check if user has owner role on event (using delete permission as proxy)
  // Backend will enforce actual owner check
  const hasOwnerRole = !!permissions?.events?.[eventIdForPermissions || '']?.includes('delete');

  // Check if contributor has owner or event_manager roles (event roles are stored in the contributor object)
  // Contributors are specific to one event, so all event roles are for the same event
  const contributorHasOwnerOrManagerRole = useMemo(() => {
    if (!contributor.roles || contributor.roles.length === 0) {
      return false;
    }

    // Check if contributor has owner/event_manager roles (all event roles are for the same event)
    return contributor.roles.some(
      (role) => role.entityType === 'Event' && (role.role === 'owner' || role.role === 'event_manager'),
    );
  }, [contributor.roles]);

  // Disable delete operations if contributor has owner/event_manager roles and user doesn't have owner role
  const canDeleteContributor = !contributorHasOwnerOrManagerRole || (contributorHasOwnerOrManagerRole && hasOwnerRole);
  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const [searchParams] = useSearchParams();
  const menuOpen = Boolean(menuElement);
  const [isDeleteContributorModalOpen, setIsDeleteContributorModalOpen] = useState(false);
  const [contributorRoleIdsToDelete, setContributorRoleIdsToDelete] = useState<string[] | undefined>(undefined);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [isFlowSlotModalOpen, setIsFlowSlotModalOpen] = useState(false);
  const [flowSlotAction, setFlowSlotAction] = useState<'create' | 'delete' | null>(null);
  const [flowSlotToManage, setFlowSlotToManage] = useState<{
    roleId: string;
    slotId?: string;
    flowSlotId: string;
    eventSlotId?: string;
  } | null>(null);
  const [isRemoteSpeakerModalOpen, setIsRemoteSpeakerModalOpen] = useState(false);
  const [isDisableRemoteSpeakerEventModalOpen, setIsDisableRemoteSpeakerEventModalOpen] = useState(false);
  const [isRemoteSpeakerScopeModalOpen, setIsRemoteSpeakerScopeModalOpen] = useState(false);
  const [remoteSpeakerScopeEnabled, setRemoteSpeakerScopeEnabled] = useState(true);

  const isEventManager = !!permissions?.events?.[eventIdForPermissions || '']?.includes(
    EVENT_PERMISSIONS.MANAGE_EVENT_ROLES,
  );
  const { hasAccess: hasRemoteSpeakershipAccess, isLoading: isRemoteSpeakershipAccessLoading } =
    useFeatureAccess(REMOTE_SPEAKERSHIP_FEATURE);
  const remoteSpeakershipAvailable = !isRemoteSpeakershipAccessLoading && hasRemoteSpeakershipAccess;
  const canConfigureFlowRemoteSpeaker = !!flowContext;
  const canConfigureRemoteSpeaker = remoteSpeakershipAvailable && (isEventManager || canConfigureFlowRemoteSpeaker);
  const pendingRemoteSpeakerDayIds = useMemo(
    () =>
      contributor.remoteSpeakerDays?.filter((entry) => entry.status === 'pending').map((entry) => entry.dayId) ?? [],
    [contributor.remoteSpeakerDays],
  );
  const remoteSpeakerConfigured = contributor.remoteSpeakerEnabled ?? false;
  const eventPlan = useEventStore((s) => s.eventPlan);
  const remoteSpeakerDayEntitlementModal = useRemoteSpeakerDayEntitlementModalState();
  const eventRemoteSpeakerHeaderIcon = useMemo(
    () =>
      resolveContributorEventRemoteSpeakerHeaderIcon({
        remoteSpeakerEnabled: contributor.remoteSpeakerEnabled,
        remoteSpeakerDays: contributor.remoteSpeakerDays,
      }),
    [contributor.remoteSpeakerEnabled, contributor.remoteSpeakerDays],
  );
  const { mutateAsync: configureRemoteSpeaker, isPending: isDisableRemoteSpeakerEventPending } =
    useConfigureRemoteSpeakerMutation();

  const profile = contributor.profiles?.[0];
  const displayName = profile?.displayName || t('event.detail.contributors.unknownContributor');
  const eventProfileAssetName = profile?.assetName;
  const providedProfilePhotoUri = contributor.userContributor?.providedProfile?.photoUri;
  const profileReviewIconColor = useMemo(
    () => getContributorProfileReviewIconColor(contributor),
    [
      contributor.id,
      contributor.userContributor?.providedProfile?.submittedVersion,
      contributor.profiles?.[0]?.reviewedSubmittedVersion,
    ],
  );
  const invitationAvatarRingColor = useMemo(
    () => getContributorInvitationRingColor(contributor),
    [contributor.id, contributor.invitedAt, contributor.acceptedAt],
  );
  const { isSmallScreen } = useScreenSize();

  // Invite staging lives on roles/slots only (see ContributorRoles); row-level checkbox duplicated that UX.
  const showLeftInviteColumn = isNewContributor;

  // Inline editing state - controlled by parent component
  const isEditing = isInlineEditing;
  const [editDisplayName, setEditDisplayName] = useState(displayName);
  const [editOrganization, setEditOrganization] = useState(profile?.organization || '');
  const [editTitle, setEditTitle] = useState(profile?.title || '');
  const [editEmail, setEditEmail] = useState(contributor?.email || '');
  const [editPhoneNumber, setEditPhoneNumber] = useState(contributor?.phoneNumber || '');

  // New contributor state
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newOrganization, setNewOrganization] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [newCrewRole, setNewCrewRole] = useState<string>('');

  // Validation state
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Refs for auto-focusing
  const newNameInputRef = useRef<HTMLInputElement>(null);
  const editEmailInputRef = useRef<HTMLInputElement>(null);

  const { mutateAsync: deleteContributorMutation, isPending: deleteContributorPending } = useDeleteContributor();
  const { mutateAsync: deleteContributorRolesMutation, isPending: deleteContributorRolesPending } =
    useDeleteEventContributorRoles();
  const { mutateAsync: deleteSessionContributorMutation, isPending: isDeleteSessionContributorPending } =
    useDeleteSessionContributor();
  const { mutateAsync: deleteFlowContributorMutation, isPending: isDeleteFlowContributorPending } =
    useDeleteFlowContributor();
  const { mutateAsync: resendInvitationMutation, isPending: resendInvitationPending } =
    useResendContributorInvitation();
  const { mutateAsync: patchContributorMutation, isPending: isPatchingContributor } = usePatchContributorMutation();
  const { mutateAsync: createContributorRoleSlot } = useCreateContributorRoleSlotMutation();
  const { mutateAsync: createSessionRoleSlotsForAllEventSlots } = useCreateSessionRoleSlotsForAllEventSlotsMutation();
  const { mutateAsync: deleteContributorRoleSlot } = useDeleteContributorRoleSlotMutation();
  const { mutateAsync: deleteRoleSlotsByEventSlot } = useDeleteContributorRoleSlotsByEventSlotMutation();
  const { mutateAsync: createFlowRoleSlotByEventSlot } = useCreateContributorFlowRoleSlotByEventSlotMutation();
  const selectedSlot = useFlowStore((s) => s.selectedSlot);
  const eventSlotIdParam = searchParams.get('eventSlotId') || undefined;
  const flowSlotId = normalizeId(flowContext?.slot?.id);
  const flowSlotOriginId = normalizeId(flowContext?.slot?.origin);
  const hasFlowSlotContext = !!(flowSlotId || flowSlotOriginId);
  const isMasterMode = !eventSlotIdParam && !hasFlowSlotContext;
  const shouldShowContributorMenu = !!flowContext && event?.multiFlow === false;
  const shouldShowContributorMoreMenu = shouldShowContributorMenu || canConfigureRemoteSpeaker;
  const flowSlotCount = useMemo(() => {
    if (!eventPlanSlots || !flowContext) return 0;
    const flowIds = new Set<string>(
      [normalizeId(flowContext.id), normalizeId(flowContext.origin)].filter(Boolean) as string[],
    );
    return eventPlanSlots.filter((slot) => {
      if (slot.contentType !== EVENT_SLOT_TYPE.FLOW) return false;
      const slotContentId = normalizeId(slot.content);
      const slotContentOrigin =
        typeof slot.content === 'object' && slot.content
          ? normalizeId((slot.content as { origin?: unknown }).origin)
          : undefined;
      if (!slotContentId && !slotContentOrigin) return false;
      return (slotContentId && flowIds.has(slotContentId)) || (slotContentOrigin && flowIds.has(slotContentOrigin));
    }).length;
  }, [eventPlanSlots, flowContext]);
  const slotOnlyFlowRemoval = !!flowContext && !isMasterMode && (!hasPlanSlotData || flowSlotCount > 1);
  const flowRoleSlotState = useMemo(() => {
    if (!flowContext) return { role: undefined, hasActiveSlot: true };
    const flowIds = new Set<string>(
      [normalizeId(flowContext.id), normalizeId(flowContext.origin)].filter(Boolean) as string[],
    );
    const flowRole = contributor.roles.find((role) => {
      if (role.entityType !== 'Flow') return false;
      const roleEntityId = normalizeId(role.entity);
      const roleEntityOrigin =
        typeof role.entity === 'object' && role.entity ? normalizeId((role.entity as { origin?: unknown }).origin) : '';
      return (roleEntityId && flowIds.has(roleEntityId)) || (roleEntityOrigin && flowIds.has(roleEntityOrigin));
    });

    if (!flowRole) return { role: undefined, hasActiveSlot: true };
    const eventSlotId = eventSlotIdParam || flowSlotId || flowSlotOriginId;
    if (!eventSlotId) return { role: flowRole, hasActiveSlot: true };
    const hasActiveSlot = !!flowRole.slots?.some(
      (slot) => slot.entityType === 'Flow' && normalizeId(slot.eventSlotId) === eventSlotId,
    );
    return { role: flowRole, hasActiveSlot };
  }, [contributor.roles, eventSlotIdParam, flowContext, flowSlotId, flowSlotOriginId]);
  const isFlowRoleInactiveInSlot = !!flowContext && !!flowRoleSlotState.role && !flowRoleSlotState.hasActiveSlot;
  const flowIdForRemoteSpeaker = flowContext
    ? flowContext.origin
      ? getObjectId(flowContext.origin)
      : flowContext.id
    : undefined;
  const menuShowsRemote = flowContext
    ? resolveFlowRemoteSpeakerMenuShowsRemote({
        role: flowRoleSlotState.role,
        useSlotContext: false,
        contributorRemoteSpeakerEnabled: remoteSpeakerConfigured,
      })
    : remoteSpeakerConfigured;

  // Flow context: find matching session role and flow slot
  const flowSlotInfo = useMemo(() => {
    if (!flowContext) return null;

    // Find session role for this contributor
    const sessionRoles = contributor.roles.filter((r) => r.entityType === 'Session');
    if (sessionRoles.length === 0) return null;

    // Find flow slot that contains the session (use selectedSlot if available, otherwise first matching)
    const targetSlot =
      selectedSlot ||
      flowContext.slots?.find((slot) => {
        if (slot.contentType !== 'Session') return false;
        const sessionId = typeof slot.content === 'string' ? slot.content : slot.content?.id;
        return sessionRoles.some((role) => {
          const roleSessionId = typeof role.entity === 'string' ? role.entity : role.entity?.id;
          return roleSessionId === sessionId;
        });
      });

    if (!targetSlot || targetSlot.contentType !== 'Session') return null;

    const sessionId = typeof targetSlot.content === 'string' ? targetSlot.content : targetSlot.content?.id;
    const matchingRole = sessionRoles.find((role) => {
      const roleSessionId = typeof role.entity === 'string' ? role.entity : role.entity?.id;
      return roleSessionId === sessionId;
    });

    if (!matchingRole) return null;

    // Check if roleslot exists for this flow slot
    // Note: deleted slots should be filtered out by the backend formatter, but check as safety
    const existingSlot = matchingRole.slots?.find((slot) => {
      if (slot.entityType !== 'FlowSlot' || slot.deleted === true) {
        return false;
      }
      // Handle both string ID and object with id property
      const slotEntityId = typeof slot.entity === 'string' ? slot.entity : slot.entity?.id;
      const matchesFlowSlot = slotEntityId === targetSlot.id;
      if (!matchesFlowSlot) return false;
      if (flowContext?.slot?.id && slot.eventSlotId) {
        return slot.eventSlotId === flowContext.slot.id || slot.eventSlotId === flowContext.slot.origin;
      }
      return true;
    });

    return {
      role: matchingRole,
      flowSlot: targetSlot,
      roleslot: existingSlot,
    };
  }, [flowContext, contributor, selectedSlot]);

  const handleFlowSlotActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!flowSlotInfo) return;

    if (flowSlotInfo.roleslot) {
      setFlowSlotAction('delete');
      setFlowSlotToManage({
        roleId: flowSlotInfo.role.id,
        slotId: flowSlotInfo.roleslot.id,
        flowSlotId: flowSlotInfo.flowSlot.id,
        eventSlotId: flowContext?.slot?.id,
      });
    } else {
      setFlowSlotAction('create');
      setFlowSlotToManage({
        roleId: flowSlotInfo.role.id,
        flowSlotId: flowSlotInfo.flowSlot.id,
        eventSlotId: flowContext?.slot?.id,
      });
    }
    setIsFlowSlotModalOpen(true);
  };

  const handleFlowSlotActionConfirm = async () => {
    if (!flowSlotToManage || !flowSlotInfo || !flowContext) return;

    try {
      if (flowSlotAction === 'create') {
        // Get sessionId from role entity (use origin if available)
        const roleSessionEntity = flowSlotInfo.role.entity;
        const sessionId =
          typeof roleSessionEntity === 'string'
            ? roleSessionEntity
            : roleSessionEntity?.origin
              ? getObjectId(roleSessionEntity.origin)
              : roleSessionEntity?.id;
        // Get flowId from flowContext (use origin if available)
        const flowIdForApi = flowContext.origin ? getObjectId(flowContext.origin) : flowContext.id;

        if (!flowSlotToManage.eventSlotId) {
          // Master mode: create roleslots for all eventSlots
          if (!sessionId || !flowIdForApi) {
            throw new Error('Missing session or flow context for role slot creation.');
          }
          await createSessionRoleSlotsForAllEventSlots({
            contributorId: contributor.id,
            roleId: flowSlotToManage.roleId,
            sessionId,
            flowId: flowIdForApi,
          });
        } else {
          // Specific eventSlot mode: create roleslot for this eventSlot
          await createContributorRoleSlot({
            contributorId: contributor.id,
            roleId: flowSlotToManage.roleId,
            flowSlotId: flowSlotToManage.flowSlotId,
            eventSlotId: flowSlotToManage.eventSlotId,
          });
        }
        const displayName = contributor.profiles?.[0]?.displayName || contributor.email || 'Contributor';
        notification.notifySuccess(
          t('event.detail.contributors.notifications.createRoleSlotSuccess', { name: displayName }),
        );
      } else if (flowSlotAction === 'delete' && flowSlotToManage.slotId) {
        await deleteContributorRoleSlot({
          contributorId: contributor.id,
          roleId: flowSlotToManage.roleId,
          slotId: flowSlotToManage.slotId,
        });
        const displayName = contributor.profiles?.[0]?.displayName || contributor.email || 'Contributor';
        notification.notifySuccess(
          t('event.detail.contributors.notifications.deleteRoleSlotSuccess', { name: displayName }),
        );
      }
      setIsFlowSlotModalOpen(false);
      setFlowSlotToManage(null);
      setFlowSlotAction(null);
    } catch (error) {
      console.error(error);
      const displayName = contributor.profiles?.[0]?.displayName || contributor.email || 'Contributor';
      notification.notifyError(
        flowSlotAction === 'create'
          ? t('event.detail.contributors.notifications.createRoleSlotError', { name: displayName })
          : t('event.detail.contributors.notifications.deleteRoleSlotError', { name: displayName }),
      );
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuElement(event.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuElement(null);
  };

  const handleRemoteSpeakerMenuAction = () => {
    if (menuShowsRemote) {
      if (flowContext) {
        setRemoteSpeakerScopeEnabled(false);
        setIsRemoteSpeakerScopeModalOpen(true);
      } else {
        setIsDisableRemoteSpeakerEventModalOpen(true);
      }
      return;
    }

    if (flowContext) {
      setRemoteSpeakerScopeEnabled(true);
      setIsRemoteSpeakerScopeModalOpen(true);
      return;
    }

    setIsRemoteSpeakerModalOpen(true);
  };

  const handleDisableRemoteSpeakerEventConfirm = async () => {
    if (!eventId) return;

    const originEventId = event?.origin ? getObjectId(event.origin) : eventId;

    try {
      await configureRemoteSpeaker({
        eventId: originEventId,
        contributorId: contributor.id,
        scope: 'event',
        enabled: false,
        applyToExistingContributions: true,
        dayIds: [],
      });
      notification.notifySuccess(
        t('session.remoteSpeaker.disabledNotification', {
          name: displayName || t('event.detail.contributors.unknownContributor'),
        }),
      );
      setIsDisableRemoteSpeakerEventModalOpen(false);
    } catch {
      notification.notifyError(
        t('session.remoteSpeaker.toggleError', {
          name: displayName || t('event.detail.contributors.unknownContributor'),
        }),
      );
    }
  };

  const handleInlineEdit = () => {
    onStartInlineEdit?.();
  };

  const handleInlineEditEmail = () => {
    onStartInlineEdit?.();
    setTimeout(() => {
      if (editEmailInputRef.current) {
        editEmailInputRef.current.focus();
        editEmailInputRef.current.select();
      }
    }, 150);
  };

  const handleInlineSave = async () => {
    if (editEmail && !emailSchema.safeParse(editEmail).success) {
      notification.notifyError(t('event.detail.contributors.validation.invalidEmail'));
      return;
    }

    if (editPhoneNumber && !phoneSchema.safeParse(editPhoneNumber).success) {
      notification.notifyError(t('event.detail.contributors.validation.invalidPhone'));
      return;
    }

    try {
      await patchContributorMutation({
        id: contributor.id,
        profiles: [
          {
            ...profile,
            displayName: editDisplayName,
            organization: editOrganization,
            title: editTitle,
          },
        ],
        ...(editEmail.trim() ? { email: editEmail.trim() } : { email: null }),
        ...(editPhoneNumber.trim() ? { phoneNumber: editPhoneNumber.trim() } : { phoneNumber: null }),
      });

      notification.notifySuccess(t('event.detail.contributors.notifications.updateContributorSuccess'));
      onStopInlineEdit?.();
    } catch (error) {
      console.error('Failed to update contributor:', error);
      notification.notifyError(t('event.detail.contributors.notifications.updateContributorFailed'));
    }
  };

  const handleInlineCancel = () => {
    setEditDisplayName(displayName);
    setEditOrganization(profile?.organization || '');
    setEditTitle(profile?.title || '');
    setEditEmail(contributor?.email || '');
    setEditPhoneNumber(contributor?.phoneNumber || '');
    setEmailError('');
    setPhoneError('');
    onStopInlineEdit?.();
  };

  const handleSaveNew = () => {
    if (!newDisplayName.trim() || !isValidDisplayName(newDisplayName)) return;

    if (newEmail.trim() && !emailSchema.safeParse(newEmail.trim()).success) {
      notification.notifyError(t('event.detail.contributors.validation.invalidEmail'));
      return;
    }

    if (newPhoneNumber.trim() && !phoneSchema.safeParse(newPhoneNumber.trim()).success) {
      notification.notifyError(t('event.detail.contributors.validation.invalidPhone'));
      return;
    }

    onSaveNew?.({
      displayName: newDisplayName.trim(),
      organization: newOrganization.trim(),
      title: newTitle.trim(),
      email: newEmail.trim(),
      phoneNumber: newPhoneNumber.trim(),
      crewRole: newCrewRole || undefined,
    });

    onStopInlineEdit?.();
  };

  const handleCancelNew = () => {
    setEmailError('');
    setPhoneError('');
    setNewCrewRole('');
    onCancelNew?.();
  };

  const handleEmailChange = (email: string, isNew: boolean = true) => {
    if (isNew) {
      setNewEmail(email);
    } else {
      setEditEmail(email);
    }

    if (email && !emailSchema.safeParse(email).success) {
      setEmailError(t('event.detail.contributors.validation.invalidEmail'));
    } else {
      setEmailError('');
    }
  };

  const handlePhoneChange = (phone: string, isNew: boolean = true) => {
    if (isNew) {
      setNewPhoneNumber(phone);
    } else {
      setEditPhoneNumber(phone);
    }

    if (phone && !phoneSchema.safeParse(phone).success) {
      setPhoneError(t('event.detail.contributors.validation.invalidPhone'));
    } else {
      setPhoneError('');
    }
  };

  useEffect(() => {
    if (isNewContributor) {
      setTimeout(() => {
        if (newNameInputRef.current) {
          newNameInputRef.current.focus();
          newNameInputRef.current.select();
        }
      }, 100);
    }
  }, [isNewContributor]);

  const deleteContributor = useCallback(async () => {
    try {
      await deleteContributorMutation(contributor.id);
      notification.notifySuccess(t('event.detail.contributors.notifications.deleteContributorSuccess'));
    } catch {
      notification.notifyError(t('event.detail.contributors.notifications.deleteContributorFailed'));
    }
  }, [contributor.id, deleteContributorMutation, notification, t]);

  const deleteContributorRoles = useCallback(async () => {
    if (!contributorRoleIdsToDelete) return;
    const activeRoleCount = contributor.roles.filter((r) => !(r as { deleted?: boolean }).deleted).length;
    const deletingAllRoles = activeRoleCount > 0 && contributorRoleIdsToDelete.length >= activeRoleCount;
    // Slot-only removal is for detaching a flow role from one event slot, not "delete all roles".
    const effectiveSlotOnlyFlowRemoval = slotOnlyFlowRemoval && !deletingAllRoles;

    try {
      const flowContextIds = new Set<string>(
        [normalizeId(flowContext?.id), normalizeId(flowContext?.origin)].filter(Boolean) as string[],
      );
      const flowRolesInContext = contributor.roles.filter((role) => {
        if (role.entityType !== 'Flow') return false;
        const roleEntityId = normalizeId(role.entity);
        const roleEntityOrigin =
          typeof role.entity === 'object' && role.entity
            ? normalizeId((role.entity as { origin?: unknown }).origin)
            : '';
        return (
          (roleEntityId && flowContextIds.has(roleEntityId)) ||
          (roleEntityOrigin && flowContextIds.has(roleEntityOrigin))
        );
      });

      const roleIdsToDelete = effectiveSlotOnlyFlowRemoval
        ? contributorRoleIdsToDelete.filter((roleId) => !flowRolesInContext.some((role) => role.id === roleId))
        : contributorRoleIdsToDelete;

      // DELETE /contributors/:id/roles only soft-deletes Event roles; Session/Flow are removed via entity APIs below.
      const eventRoleIdsToDelete = roleIdsToDelete.filter((roleId) => {
        const role = contributor.roles.find((r) => r.id === roleId);
        return role?.entityType === 'Event';
      });

      if (eventRoleIdsToDelete.length > 0) {
        await deleteContributorRolesMutation({
          contributorId: contributor.id,
          roleIds: eventRoleIdsToDelete,
        });
      }

      if (effectiveSlotOnlyFlowRemoval) {
        const eventSlotId = eventSlotIdParam || flowSlotId || flowSlotOriginId;
        if (!eventSlotId) {
          throw new Error('Missing event slot context for flow role removal.');
        }
        await Promise.all(
          flowRolesInContext.map((role) =>
            deleteRoleSlotsByEventSlot({
              contributorId: contributor.id,
              roleId: role.id,
              eventSlotId,
            }),
          ),
        );
      }

      const flowIdsScheduledForRemoval = new Set<string>();
      await Promise.allSettled(
        contributor.roles.reduce<Promise<void>[]>((promises, role) => {
          if ((role as { deleted?: boolean }).deleted || !role.entity) return promises;
          const entityId = getObjectId(role.entity);
          if (role.entityType === 'Session') {
            promises.push(
              deleteSessionContributorMutation({
                sessionId: entityId,
                contributorId: contributor.id,
              }),
            );
          } else if (role.entityType === 'Flow') {
            const isFlowRoleInContext = flowRolesInContext.some((flowRole) => flowRole.id === role.id);
            if (effectiveSlotOnlyFlowRemoval && isFlowRoleInContext) {
              return promises;
            }
            if (flowIdsScheduledForRemoval.has(entityId)) {
              return promises;
            }
            flowIdsScheduledForRemoval.add(entityId);
            promises.push(
              deleteFlowContributorMutation({
                flowId: entityId,
                contributorId: contributor.id,
              }),
            );
          }
          return promises;
        }, []),
      );

      const successMessage = effectiveSlotOnlyFlowRemoval
        ? t('event.detail.contributors.notifications.deleteRoleSlotSuccess', { name: displayName || '-' })
        : t('event.detail.contributors.notifications.deleteContributorRolesSuccess');
      notification.notifySuccess(successMessage);
      setContributorRoleIdsToDelete(undefined);
    } catch {
      const errorMessage = effectiveSlotOnlyFlowRemoval
        ? t('event.detail.contributors.notifications.deleteRoleSlotError', { name: displayName || '-' })
        : t('event.detail.contributors.notifications.deleteContributorRolesError');
      notification.notifyError(errorMessage);
    }
  }, [
    contributorRoleIdsToDelete,
    contributor.id,
    contributor.roles,
    deleteContributorRolesMutation,
    deleteFlowContributorMutation,
    deleteSessionContributorMutation,
    deleteRoleSlotsByEventSlot,
    notification,
    t,
    flowContext,
    slotOnlyFlowRemoval,
    eventSlotIdParam,
    flowSlotId,
    flowSlotOriginId,
    displayName,
  ]);

  const canInvite = !!permissions?.events?.[eventId!]?.includes(EVENT_PERMISSIONS.INVITE_CONTRIBUTORS);
  const hasAnyRoleOrSlotInvited = contributor.roles.some((role) => {
    if ((role as { deleted?: boolean }).deleted) return false;
    return !!role.invitedAt || (role.slots?.some((s) => s.deleted !== true && !!s.invitedAt) ?? false);
  });
  const hasInvitedRoleOrSlotNotAccepted = contributor.roles.some((role) => {
    if ((role as { deleted?: boolean }).deleted) return false;
    if (role.invitedAt && !role.acceptedAt) return true;
    return role.slots?.some((slot) => slot.deleted !== true && !!slot.invitedAt && !slot.acceptedAt) ?? false;
  });
  /** Event-level invite only applies before any role/slot invite exists (same row actions as “no role/slot invites yet”). */
  const contributorEventInvitePendingResend =
    !!contributor.invitedAt && !contributor.acceptedAt && !hasAnyRoleOrSlotInvited;
  const canResend = canInvite && (hasInvitedRoleOrSlotNotAccepted || contributorEventInvitePendingResend);
  const hasSelfStaged = contributorHasInviteableStagedSelection(contributor, flowContext, invitePublicationCtx);
  const canSendStagedInvite = canInvite && hasSelfStaged && !!contributor.email?.trim();
  const sendButtonEnabled = canSendStagedInvite || canResend;
  const canCopyInviteLink = sendButtonEnabled;
  /** Info for staged “send new invites”; secondary when only resend / invited-not-yet-accepted (reversed emphasis). */
  const inviteHeaderIconsUseEmphasis = sendButtonEnabled && canSendStagedInvite;

  /** Match compact 34×34 header actions on all viewports when `isSmallScreen` (no 40–44px “large touch” tier). */
  const headerActionIconSx = isSmallScreen ? { p: 0.25, minWidth: 34, minHeight: 34 } : contributorRowHeaderIconSx;

  return (
    <>
      <Box
        sx={{
          'pt': '8px',
          'px': '8px',
          'pb': isSmallScreen ? '8px' : { xs: 2, sm: 1.5 },
          'display': 'flex',
          'flexDirection': 'row',
          'gap': isSmallScreen ? 1 : { xs: 1.25, sm: 1 },
          'alignItems': 'flex-start',
          'border': 1,
          'borderColor': 'divider',
          'borderRadius': 2,
          'boxShadow': 1,
          'backgroundColor': (theme) => theme.palette.background.elevation1,
          'transition': 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          'mb': 2,
          'mt': 1,
          'opacity': isFlowRoleInactiveInSlot ? 0.5 : 1,
          'pointerEvents': 'auto',
          '@media (hover: hover)': {
            '&:hover': {
              boxShadow: 2,
              borderColor: 'divider',
            },
          },
        }}
        data-testid="contributor-row"
      >
        {/* Left: new-contributor marker only (invite staging is per role/slot in ContributorRoles). */}
        {showLeftInviteColumn && (
          <Box
            sx={{
              flexShrink: 0,
              width: 44,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              pt: { xs: 0.25, sm: 0 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: { xs: 44, sm: 36 },
              }}
            >
              <PersonAdd color="action" fontSize="small" />
            </Box>
          </Box>
        )}

        {/* Middle section: Contributor data + Action buttons + Roles wrapper */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            flexWrap: { lg: 'wrap' },
            gap: { xs: isSmallScreen ? 1.25 : 2, lg: 2 },
            flex: '1 1 0%',
            minWidth: 0,
          }}
        >
          {/* Contributor data + Action buttons */}
          <Box
            sx={{
              flex: { xs: '1 1 auto', lg: '0 1 520px' },
              minWidth: 0,
              maxWidth: { xs: '100%', lg: '520px' },
              ...(!isSmallScreen && { alignSelf: 'stretch', mx: '8px' }),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: { xs: 1.25, sm: 1.5 },
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {!isNewContributor && (
                  <ProfilePhotoAvatar
                    contributorId={eventProfileAssetName ? contributor.id : undefined}
                    assetName={eventProfileAssetName}
                    src={eventProfileAssetName ? undefined : providedProfilePhotoUri || undefined}
                    displayName={displayName}
                    variant="circular"
                    onClick={
                      onOpenEventProfileEditor
                        ? (e) => {
                            e.stopPropagation();
                            onOpenEventProfileEditor();
                          }
                        : undefined
                    }
                    sx={(theme) => ({
                      width: { xs: 44, sm: 40 },
                      height: { xs: 44, sm: 40 },
                      flexShrink: 0,
                      fontSize: '0.875rem',
                      ...(onOpenEventProfileEditor ? { cursor: 'pointer' } : {}),
                      ...(invitationAvatarRingColor === 'info' || invitationAvatarRingColor === 'success'
                        ? { boxShadow: `0 0 0 2px ${theme.palette[invitationAvatarRingColor].main}` }
                        : {}),
                    })}
                    role={onOpenEventProfileEditor ? 'button' : undefined}
                    aria-label={onOpenEventProfileEditor ? t('event.detail.contributors.manageProfile') : undefined}
                    tabIndex={onOpenEventProfileEditor ? 0 : undefined}
                    onKeyDown={
                      onOpenEventProfileEditor
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              onOpenEventProfileEditor();
                            }
                          }
                        : undefined
                    }
                  />
                )}
                <Box sx={{ flex: '1 1 auto', minWidth: 0, maxWidth: '100%' }}>
                  {isNewContributor ? (
                    <TextField
                      inputRef={newNameInputRef}
                      size="small"
                      variant="standard"
                      placeholder={t('event.detail.contributors.namePlaceholder')}
                      value={newDisplayName}
                      onChange={(e) => setNewDisplayName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSaveNew();
                        }
                      }}
                      sx={{
                        'width': '100%',
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem',
                          lineHeight: '1.25rem',
                          padding: '6px 8px',
                        },
                        '& .MuiInputBase-root': {
                          minHeight: '32px',
                        },
                      }}
                    />
                  ) : isEditing ? (
                    <TextField
                      size="small"
                      variant="standard"
                      value={editDisplayName}
                      onChange={(e) => setEditDisplayName(e.target.value)}
                      sx={{
                        'width': '100%',
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem',
                          lineHeight: '1.25rem',
                          padding: '6px 8px',
                        },
                        '& .MuiInputBase-root': {
                          minHeight: '32px',
                        },
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <HighlightedText
                        text={displayName}
                        searchTerm={searchTerm}
                        variant="subtitle2"
                        component="div"
                        sx={{ fontWeight: 600, lineHeight: 1.35 }}
                      />
                      <IconButton
                        size="small"
                        onClick={handleInlineEdit}
                        data-testid="inline-edit-contributor-button"
                        title={t('event.detail.contributors.inlineEditTooltip')}
                        sx={{
                          'padding': '1px',
                          'minWidth': '20px',
                          'width': '20px',
                          'height': '20px',
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                      >
                        <Edit sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </Box>
                  )}

                  {isNewContributor ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.25 }}>
                      <TextField
                        size="small"
                        variant="standard"
                        placeholder={t('event.detail.contributors.titleInputLabel')}
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSaveNew();
                          }
                        }}
                        sx={{
                          'minWidth': '200px',
                          'flex': '1 1 200px',
                          '& .MuiInputBase-input': {
                            fontSize: '0.875rem',
                            lineHeight: '1.25rem',
                            padding: '6px 8px',
                          },
                          '& .MuiInputBase-root': {
                            minHeight: '32px',
                          },
                        }}
                      />
                      <TextField
                        size="small"
                        variant="standard"
                        placeholder={t('event.detail.contributors.organizationPlaceholder')}
                        value={newOrganization}
                        onChange={(e) => setNewOrganization(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSaveNew();
                          }
                        }}
                        sx={{
                          'minWidth': '200px',
                          'flex': '1 1 200px',
                          '& .MuiInputBase-input': {
                            fontSize: '0.875rem',
                            lineHeight: '1.25rem',
                            padding: '6px 8px',
                          },
                          '& .MuiInputBase-root': {
                            minHeight: '32px',
                          },
                        }}
                      />
                    </Box>
                  ) : isEditing ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.25 }}>
                      <TextField
                        size="small"
                        variant="standard"
                        placeholder={t('event.detail.contributors.titleInputLabel')}
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        sx={{
                          'minWidth': '200px',
                          'flex': '1 1 200px',
                          '& .MuiInputBase-input': {
                            fontSize: '0.875rem',
                            lineHeight: '1.25rem',
                            padding: '6px 8px',
                          },
                          '& .MuiInputBase-root': {
                            minHeight: '32px',
                          },
                        }}
                      />
                      <TextField
                        size="small"
                        variant="standard"
                        placeholder={t('event.detail.contributors.organizationPlaceholder')}
                        value={editOrganization}
                        onChange={(e) => setEditOrganization(e.target.value)}
                        sx={{
                          'minWidth': '200px',
                          'flex': '1 1 200px',
                          '& .MuiInputBase-input': {
                            fontSize: '0.875rem',
                            lineHeight: '1.25rem',
                            padding: '6px 8px',
                          },
                          '& .MuiInputBase-root': {
                            minHeight: '32px',
                          },
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{ mt: 0, display: 'flex', flexWrap: 'wrap', rowGap: 0, columnGap: 0.5, alignItems: 'center' }}
                    >
                      {profile?.title && (
                        <HighlightedText
                          text={`${profile.title},`}
                          searchTerm={searchTerm}
                          variant="body2"
                          color="text.secondary"
                        />
                      )}
                      {profile?.organization && (
                        <HighlightedText
                          text={profile.organization}
                          searchTerm={searchTerm}
                          variant="body2"
                          color="text.secondary"
                        />
                      )}
                    </Box>
                  )}

                  {isNewContributor ? (
                    <>
                      <Box sx={{ mt: 0.25, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <TextField
                          size="small"
                          variant="standard"
                          type="email"
                          placeholder={t('event.detail.contributors.emailPlaceholder')}
                          value={newEmail}
                          onChange={(e) => handleEmailChange(e.target.value, true)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveNew();
                            }
                          }}
                          error={!!emailError}
                          helperText={emailError}
                          sx={{
                            'minWidth': '200px',
                            'flex': '1 1 200px',
                            '& .MuiInputBase-input': {
                              fontSize: '0.875rem',
                              lineHeight: '1.25rem',
                              padding: '6px 8px',
                            },
                            '& .MuiInputBase-root': {
                              minHeight: '32px',
                            },
                          }}
                        />
                        <TextField
                          size="small"
                          variant="standard"
                          type="tel"
                          placeholder={t('event.detail.contributors.phonePlaceholder')}
                          value={newPhoneNumber}
                          onChange={(e) => handlePhoneChange(e.target.value, true)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveNew();
                            }
                          }}
                          error={!!phoneError}
                          helperText={phoneError}
                          sx={{
                            'minWidth': '200px',
                            'flex': '1 1 200px',
                            '& .MuiInputBase-input': {
                              fontSize: '0.875rem',
                              lineHeight: '1.25rem',
                              padding: '6px 8px',
                            },
                            '& .MuiInputBase-root': {
                              minHeight: '32px',
                            },
                          }}
                        />
                      </Box>
                      <Box sx={{ mt: 0.25 }}>
                        <FormControl
                          fullWidth
                          variant="standard"
                          size="small"
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: '0.875rem',
                              lineHeight: '1.25rem',
                              padding: '6px 8px',
                            },
                            '& .MuiInputBase-root': {
                              minHeight: '32px',
                            },
                          }}
                        >
                          <Select
                            value={newCrewRole}
                            onChange={(e) => setNewCrewRole(e.target.value)}
                            displayEmpty
                            size="small"
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  '& .MuiMenuItem-root': {
                                    fontSize: '0.875rem',
                                    lineHeight: '1.25rem',
                                    padding: '6px 8px',
                                    minHeight: '32px',
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value="">
                              <em>{t('event.detail.contributors.noEventRole')}</em>
                            </MenuItem>
                            {CREW_ROLES.map((role: string) => {
                              // Disable event_manager and owner roles if user doesn't have owner role
                              const isRestrictedRole = role === 'event_manager' || role === 'owner';
                              const isDisabled = isRestrictedRole && !hasOwnerRole;

                              return (
                                <MenuItem key={role} value={role} disabled={isDisabled}>
                                  {t(`common.roleLabels.${role}`)}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Box>
                    </>
                  ) : isEditing ? (
                    <Box sx={{ mt: 0.25, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <TextField
                        inputRef={editEmailInputRef}
                        size="small"
                        variant="standard"
                        type="email"
                        placeholder={t('event.detail.contributors.emailPlaceholder')}
                        value={editEmail}
                        onChange={(e) => handleEmailChange(e.target.value, false)}
                        error={!!emailError}
                        helperText={emailError}
                        sx={{
                          'minWidth': '200px',
                          'flex': '1 1 200px',
                          '& .MuiInputBase-input': {
                            fontSize: '0.875rem',
                            lineHeight: '1.25rem',
                            padding: '6px 8px',
                          },
                          '& .MuiInputBase-root': {
                            minHeight: '32px',
                          },
                        }}
                      />
                      <TextField
                        size="small"
                        variant="standard"
                        type="tel"
                        placeholder={t('event.detail.contributors.phonePlaceholder')}
                        value={editPhoneNumber}
                        onChange={(e) => handlePhoneChange(e.target.value, false)}
                        error={!!phoneError}
                        helperText={phoneError}
                        sx={{
                          'minWidth': '200px',
                          'flex': '1 1 200px',
                          '& .MuiInputBase-input': {
                            fontSize: '0.875rem',
                            lineHeight: '1.25rem',
                            padding: '6px 8px',
                          },
                          '& .MuiInputBase-root': {
                            minHeight: '32px',
                          },
                        }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ mt: 0.25, display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {contributor?.email ? (
                        <Typography variant="body2" color="text.secondary">
                          {contributor.email}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          color="info.main"
                          sx={{
                            'fontStyle': 'italic',
                            'cursor': 'pointer',
                            '&:hover': {
                              color: 'info.dark',
                            },
                          }}
                          onClick={handleInlineEditEmail}
                          data-testid="add-email-to-invite-clickable"
                        >
                          {t('event.detail.contributors.addEmailToInvite')}
                        </Typography>
                      )}
                      {contributor?.phoneNumber && (
                        <Typography variant="body2" color="text.secondary">
                          {contributor.phoneNumber}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Action buttons — top-aligned with avatar/name row on all breakpoints */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end',
                  flexShrink: 0,
                  flexWrap: 'wrap',
                  rowGap: isSmallScreen ? 0 : 0.25,
                  columnGap: '2px',
                  p: 0,
                  m: 0,
                  pt: isSmallScreen ? 0 : { xs: 0.125, sm: 0 },
                }}
              >
                {isFlowRoleInactiveInSlot && flowRoleSlotState.role && (
                  <IconButton
                    size="small"
                    onClick={async (event) => {
                      event.stopPropagation();
                      const eventSlotId = eventSlotIdParam || flowSlotId || flowSlotOriginId;
                      if (!eventSlotId) return;
                      try {
                        await createFlowRoleSlotByEventSlot({
                          contributorId: contributor.id,
                          roleId: flowRoleSlotState.role.id,
                          eventSlotId,
                        });
                        const name = contributor.profiles?.[0]?.displayName || contributor.email || 'Contributor';
                        notification.notifySuccess(
                          t('event.detail.contributors.notifications.createRoleSlotSuccess', { name }),
                        );
                      } catch {
                        const name = contributor.profiles?.[0]?.displayName || contributor.email || 'Contributor';
                        notification.notifyError(
                          t('event.detail.contributors.notifications.createRoleSlotError', { name }),
                        );
                      }
                    }}
                    title={t('event.detail.contributors.createRoleSlot')}
                    sx={headerActionIconSx}
                  >
                    <AddCircleOutlined color="action" />
                  </IconButton>
                )}
                {!isFlowRoleInactiveInSlot && (
                  <>
                    {flowContext && selectedSlot && flowSlotInfo && !isNewContributor && !isEditing && (
                      <IconButton
                        size="small"
                        onClick={handleFlowSlotActionClick}
                        title={
                          flowSlotInfo.roleslot
                            ? t('event.detail.contributors.deleteRoleSlot')
                            : t('event.detail.contributors.createRoleSlot')
                        }
                        sx={headerActionIconSx}
                      >
                        {flowSlotInfo.roleslot ? (
                          <RemoveCircleOutlined color="error" />
                        ) : (
                          <AddCircleOutlined color="success" />
                        )}
                      </IconButton>
                    )}
                    {isNewContributor ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 1,
                        }}
                      >
                        <IconButton
                          color="success"
                          onClick={handleSaveNew}
                          disabled={!newDisplayName.trim() || !isValidDisplayName(newDisplayName)}
                          data-testid="save-new-contributor-button"
                        >
                          <Check />
                        </IconButton>
                        <IconButton color="error" onClick={handleCancelNew} data-testid="cancel-new-contributor-button">
                          <Close />
                        </IconButton>
                      </Box>
                    ) : !isEditing ? (
                      <>
                        <Tooltip
                          title={
                            canResend
                              ? t('event.detail.contributors.resendInvitationEmailTooltip')
                              : t('event.detail.contributors.sendInvitationEmailTooltip')
                          }
                          disableHoverListener={!sendButtonEnabled}
                        >
                          <span>
                            <IconButton
                              size="small"
                              data-testid="invite-contributor-button"
                              disabled={!sendButtonEnabled}
                              onClick={() => {
                                if (canSendStagedInvite) {
                                  onInviteContributor();
                                } else if (canResend) {
                                  setIsResendModalOpen(true);
                                }
                              }}
                              sx={(theme) => ({
                                ...headerActionIconSx,
                                'color': inviteHeaderIconsUseEmphasis
                                  ? theme.palette.primary.main
                                  : sendButtonEnabled
                                    ? theme.palette.text.secondary
                                    : theme.palette.action.disabled,
                                'bgcolor': 'transparent',
                                '&:hover': {
                                  bgcolor: theme.palette.action.hover,
                                },
                                '& > svg': {
                                  fontSize: '1.5rem',
                                  width: '1.5rem',
                                  height: '1.5rem',
                                },
                              })}
                            >
                              <ForwardToInboxOutlined color="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip
                          title={t('event.detail.contributors.copyInviteLinkTooltip')}
                          disableHoverListener={!canCopyInviteLink}
                        >
                          <span>
                            <IconButton
                              size="small"
                              data-testid="copy-invite-link-button"
                              disabled={!canCopyInviteLink}
                              onClick={() => onCopyInviteLink()}
                              sx={(theme) => ({
                                ...headerActionIconSx,
                                'color': inviteHeaderIconsUseEmphasis
                                  ? theme.palette.primary.main
                                  : sendButtonEnabled
                                    ? theme.palette.text.secondary
                                    : theme.palette.action.disabled,
                                'bgcolor': 'transparent',
                                '&:hover': {
                                  bgcolor: theme.palette.action.hover,
                                },
                                '& > svg': {
                                  fontSize: '1.5rem',
                                  width: '1.5rem',
                                  height: '1.5rem',
                                },
                              })}
                            >
                              <LinkOutlined color="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title={t('event.detail.contributors.profileReviewTooltip')}>
                          <IconButton
                            size="small"
                            color={profileReviewIconColor}
                            onClick={() => onOpenEventProfileEditor?.()}
                            data-testid="edit-contributor-button"
                            sx={{
                              ...headerActionIconSx,
                              'display': { xs: shouldShowContributorMoreMenu ? 'none' : 'flex', sm: 'flex' },
                              '& > svg': {
                                fontSize: '1.5rem',
                                width: '1.5rem',
                                height: '1.5rem',
                              },
                            }}
                          >
                            <RateReviewOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!isNewContributor ? (
                          <ContributorEventRemoteSpeakerHeaderIcon
                            t={t}
                            showIcon={remoteSpeakershipAvailable && eventRemoteSpeakerHeaderIcon.showIcon}
                            iconColor={eventRemoteSpeakerHeaderIcon.color}
                            onPendingClick={
                              eventRemoteSpeakerHeaderIcon.color === 'primary' && pendingRemoteSpeakerDayIds.length > 0
                                ? () => {
                                    remoteSpeakerDayEntitlementModal.openForContributorDay(
                                      contributor,
                                      pendingRemoteSpeakerDayIds[0],
                                      event ?? eventPlan,
                                    );
                                  }
                                : undefined
                            }
                          />
                        ) : null}
                        {shouldShowContributorMoreMenu && (
                          <IconButton
                            size="small"
                            data-testid="contributor-menu-button"
                            aria-label={t('event.detail.contributors.moreOptions')}
                            id={`contributor-menu-button-${contributor.id}`}
                            aria-controls={menuOpen ? `contributor-menu-${contributor.id}` : undefined}
                            aria-expanded={menuOpen ? 'true' : undefined}
                            aria-haspopup="true"
                            onClick={handleMenuClick}
                            sx={(theme) => ({
                              ...headerActionIconSx,
                              'color': theme.palette.text.secondary,
                              'bgcolor': 'transparent',
                              '&:hover': {
                                bgcolor: theme.palette.action.hover,
                              },
                              '& > svg': {
                                fontSize: '1.5rem',
                                width: '1.5rem',
                                height: '1.5rem',
                              },
                            })}
                          >
                            <MoreVert />
                          </IconButton>
                        )}
                      </>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 1,
                        }}
                      >
                        <IconButton
                          color="success"
                          onClick={handleInlineSave}
                          disabled={isPatchingContributor}
                          data-testid="save-contributor-button"
                        >
                          <Check />
                        </IconButton>
                        <IconButton color="error" onClick={handleInlineCancel} data-testid="cancel-contributor-button">
                          <Close />
                        </IconButton>
                      </Box>
                    )}
                    {shouldShowContributorMoreMenu && (
                      <Menu
                        id={`contributor-menu-${contributor.id}`}
                        anchorEl={menuElement}
                        open={menuOpen}
                        onClose={handleMenuClose}
                        slotProps={{
                          list: {
                            'aria-labelledby': `contributor-menu-button-${contributor.id}`,
                          },
                        }}
                      >
                        {shouldShowContributorMenu ? (
                          <MenuItem
                            data-testid="contributor-profile-button"
                            onClick={() => {
                              handleMenuClose();
                              onOpenEventProfileEditor?.();
                            }}
                            sx={{ display: { xs: 'flex', sm: 'none' } }}
                          >
                            <ListItemIcon>
                              <RateReviewOutlined
                                fontSize="small"
                                color={profileReviewIconColor === 'default' ? 'action' : profileReviewIconColor}
                              />
                            </ListItemIcon>
                            <ListItemText>{t('event.detail.contributors.profile')}</ListItemText>
                          </MenuItem>
                        ) : null}
                        {canConfigureRemoteSpeaker ? (
                          <MenuItem
                            data-testid="contributor-overflow-remote-speaker"
                            onClick={() => {
                              handleMenuClose();
                              handleRemoteSpeakerMenuAction();
                            }}
                          >
                            <ListItemIcon>
                              {menuShowsRemote ? (
                                <PhoneDisabledOutlined fontSize="small" />
                              ) : (
                                <PhoneCallback fontSize="small" sx={{ transform: 'scaleX(-1)' }} />
                              )}
                            </ListItemIcon>
                            <ListItemText primary={contributorRemoteSpeakerActionLabel(t, menuShowsRemote)} />
                          </MenuItem>
                        ) : null}
                        {shouldShowContributorMenu
                          ? [
                              <MenuItem
                                key="delete-contributor"
                                data-testid="delete-contributor-button"
                                onClick={() => {
                                  handleMenuClose();
                                  setIsDeleteContributorModalOpen(true);
                                }}
                                disabled={
                                  !permissions?.contributors?.[contributor.id!]?.includes(
                                    CONTRIBUTOR_PERMISSIONS.DELETE_CONTRIBUTOR,
                                  ) || !canDeleteContributor
                                }
                              >
                                <ListItemIcon>
                                  {slotOnlyFlowRemoval ? <RemoveCircleOutlined color="action" /> : <DeleteOutlined />}
                                </ListItemIcon>
                                <ListItemText>{t('event.detail.contributors.deleteContributor')}</ListItemText>
                              </MenuItem>,
                              <MenuItem
                                key="delete-contributor-roles"
                                data-testid="delete-contributor-roles-button"
                                onClick={() => {
                                  handleMenuClose();
                                  setContributorRoleIdsToDelete(
                                    contributor.roles
                                      .filter((r) => !(r as { deleted?: boolean }).deleted)
                                      .map((r) => r.id),
                                  );
                                }}
                                disabled={
                                  contributor.roles.length === 0 ||
                                  !permissions?.contributors?.[contributor.id!]?.includes(
                                    CONTRIBUTOR_PERMISSIONS.UPDATE_CONTRIBUTOR,
                                  ) ||
                                  !canDeleteContributor
                                }
                              >
                                <ListItemIcon>
                                  <GroupRemove />
                                </ListItemIcon>
                                <ListItemText>{t('event.detail.contributors.deleteAllRoles')}</ListItemText>
                              </MenuItem>,
                            ]
                          : null}
                      </Menu>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {/* Roles section */}
          {!isFlowRoleInactiveInSlot && (
            <Box
              sx={{
                display: isNewContributor ? { xs: 'none', lg: 'flex' } : 'flex',
                flex: { xs: '1 1 auto', lg: '1 1 0%' },
                minWidth: 0,
                flexDirection: 'column',
                gap: 0,
                justifyContent: 'flex-start',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                // Subtle tint only — strong action.hover alpha read as a heavy “dark slab” on mobile / dark UI
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.06)
                    : alpha(theme.palette.common.black, 0.035),
                ...(isSmallScreen
                  ? {
                      alignSelf: 'stretch',
                      maxWidth: '100%',
                      // Stay inside contributor-row px (8px); negative margins used to “bleed” canceled that inset
                      px: 1,
                      py: 1,
                    }
                  : {
                      alignSelf: 'stretch',
                      maxWidth: { xs: '100%', lg: '520px' },
                      mx: '8px',
                      p: { xs: 1.5, sm: 1.25 },
                    }),
              }}
            >
              {!isNewContributor && (
                <ContributorRoles
                  contributor={contributor}
                  onContributonSelected={onContributionSelected}
                  searchTerm={searchTerm}
                  flowContext={flowContext}
                  compactStatusColumn={isSmallScreen}
                  canConfigureRemoteSpeaker={canConfigureRemoteSpeaker}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
      <ConfirmationModal
        open={isDeleteContributorModalOpen}
        title={t('event.detail.contributors.deleteContributorModalTitle')}
        isLoading={deleteContributorPending}
        text={t('event.detail.contributors.deleteContributorModalText', { name: displayName || '-' })}
        onConfirm={deleteContributor}
        onCancel={() => setIsDeleteContributorModalOpen(false)}
      />
      <ConfirmationModal
        open={isResendModalOpen}
        title={t('event.detail.contributors.resendInvitationModalTitle')}
        isLoading={resendInvitationPending}
        text={t('event.detail.contributors.resendInvitationModalText')}
        onConfirm={async () => {
          try {
            await resendInvitationMutation({ contributorId: contributor.id, eventId: eventId! });
            setIsResendModalOpen(false);
            notification.notifySuccess(t('event.detail.contributors.notifications.sendInvitationsSuccess'));
          } catch {
            notification.notifyError(t('event.detail.contributors.notifications.sendInvitationsFailed'));
          }
        }}
        onCancel={() => setIsResendModalOpen(false)}
      />
      <ConfirmationModal
        open={!!contributorRoleIdsToDelete}
        title={t('event.detail.contributors.deleteContributorRolesModalTitle', { name: displayName || '-' })}
        isLoading={deleteContributorRolesPending || isDeleteSessionContributorPending || isDeleteFlowContributorPending}
        text={t('event.detail.contributors.deleteContributorRolesModalText', { name: displayName || '-' })}
        onConfirm={deleteContributorRoles}
        onCancel={() => setContributorRoleIdsToDelete(undefined)}
      />
      {flowSlotToManage && (
        <ConfirmationModal
          open={isFlowSlotModalOpen}
          title={
            flowSlotAction === 'create'
              ? t('event.detail.contributors.createRoleSlotModalTitle', {
                  name: contributor.profiles?.[0]?.displayName || contributor.email || 'Contributor',
                })
              : t('event.detail.contributors.deleteRoleSlotModalTitle', {
                  name: contributor.profiles?.[0]?.displayName || contributor.email || 'Contributor',
                })
          }
          text={
            flowSlotAction === 'create'
              ? t('event.detail.contributors.createRoleSlotModalText', {
                  name: contributor.profiles?.[0]?.displayName || contributor.email || 'Contributor',
                })
              : t('event.detail.contributors.deleteRoleSlotModalText', {
                  name: contributor.profiles?.[0]?.displayName || contributor.email || 'Contributor',
                })
          }
          onConfirm={handleFlowSlotActionConfirm}
          onCancel={() => {
            setIsFlowSlotModalOpen(false);
            setFlowSlotToManage(null);
            setFlowSlotAction(null);
          }}
        />
      )}
      {!isNewContributor ? (
        <>
          <RemoteSpeakerConfigureModal
            open={isRemoteSpeakerModalOpen}
            onClose={() => setIsRemoteSpeakerModalOpen(false)}
            contributor={contributor}
            enabled
            scope="event"
            daysReadOnly
            flowContext={flowContext}
          />
          <ConfirmationModal
            open={isDisableRemoteSpeakerEventModalOpen}
            title={t('event.detail.contributors.remoteSpeaker.disableTitle', {
              name: displayName || t('event.detail.contributors.unknownContributor'),
            })}
            text={t('event.detail.contributors.remoteSpeaker.disableDescription', {
              name: displayName || t('event.detail.contributors.unknownContributor'),
            })}
            isLoading={isDisableRemoteSpeakerEventPending}
            onConfirm={handleDisableRemoteSpeakerEventConfirm}
            onCancel={() => setIsDisableRemoteSpeakerEventModalOpen(false)}
          />
          {flowContext && flowIdForRemoteSpeaker && flowRoleSlotState.role?.id ? (
            <RemoteSpeakerScopeModal
              open={isRemoteSpeakerScopeModalOpen}
              onClose={() => setIsRemoteSpeakerScopeModalOpen(false)}
              contributor={contributor}
              enabled={remoteSpeakerScopeEnabled}
              flowId={flowIdForRemoteSpeaker}
              roleId={flowRoleSlotState.role.id}
              showSlotOption={false}
            />
          ) : null}
        </>
      ) : null}
      {remoteSpeakerDayEntitlementModal.contributor ? (
        <RemoteSpeakerDayEntitlementModal
          open={remoteSpeakerDayEntitlementModal.open}
          onClose={remoteSpeakerDayEntitlementModal.close}
          contributor={remoteSpeakerDayEntitlementModal.contributor}
          dayIds={remoteSpeakerDayEntitlementModal.dayIds}
          dayLabel={remoteSpeakerDayEntitlementModal.dayLabel}
          isEventManager={isEventManager}
        />
      ) : null}
    </>
  );
};
